import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Tabs,
  Tab,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
  CircularProgress,
  TablePagination,
  Tooltip,
  Menu,
  MenuItem,
  Stack,
  TextField,
  InputAdornment,
  Select,
  FormControl
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/CloudDownloadOutlined';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Papa from 'papaparse';
import ExcelJS from 'exceljs';

import { useNotifier } from '../../../../components/Notifier';
import { createTireInspection, revalidateTireInspectionList } from '../../../../stores/tireInspectionStore';
import { useTireBatchList, revalidateTireBatchList } from '../../../../stores/tireBatchStore';
import TabTireInspectionAddEdit from './TabTireInspectionAddEdit';
import TabTireBulkInspection from './TabTireBulkInspection';
import TireActionList from './TireActionList';
import TireInspectionHistory from './TireInspectionHistory';
import { useLanguage } from '../../../../utils/i18n';

// ── Export helpers ──
const getFormattedDate = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};

const getExportColumns = (t) => [
  { field: 'inspection_date', headerName: t('tireRepairScrap.date', 'Date') },
  { field: 'serial_number', headerName: t('tireRepairScrap.serialNumberExport', 'Serial Number') },
  { field: 'specification', headerName: t('menu.specification', 'Specification') },
  { field: 'vehicle_license_plate', headerName: t('tireCardHistory.vehicle', 'Vehicle') },
  { field: 'position', headerName: t('tireRepairScrap.position', 'Position') },
  { field: 'km_unit', headerName: t('tireRepairScrap.kmUnit', 'KM Unit') },
  { field: 'hm_unit', headerName: t('tireRepairScrap.hmUnit', 'HM Unit') },
  { field: 'air_pressure', headerName: t('tireCardHistory.airPressurePsi', 'Air Pressure (psi)') },
  { field: 'tread_depth', headerName: t('tireCardHistory.treadDepthMm', 'Tread Depth (mm)') },
  { field: 'tire_damage_name', headerName: t('tireCardHistory.tireDamage', 'Damage') },
  { field: 'status_backlog', headerName: t('tireRepairScrap.priority', 'Priority') },
  { field: 'inspector_name', headerName: t('tireRepairScrap.mechanic', 'Inspector Name') },
  { field: 'notes', headerName: t('tireRepairScrap.notes', 'Notes') }
];

const getCellValueForExport = (row, col) => {
  if (col.field === 'inspection_date') {
    return row.inspection_date
      ? dayjs(row.inspection_date).format('YYYY-MM-DD')
      : row.created_date
        ? dayjs(row.created_date).format('YYYY-MM-DD')
        : '-';
  }
  if (col.field === 'specification') {
    return `${row.join?.tire_item_tire_brand_name || '-'} / ${row.join?.tire_item_tire_size_code_internal} || ${row.join?.tire_item_tire_size_code} || '-'} / ${row.join?.tire_item_tire_spec_name || '-'}`;
  }
  if (col.field === 'vehicle_license_plate') {
    return row.last_vehicle || row.join?.vehicle_license_plate || '-';
  }
  if (col.field === 'position') {
    return row.position || row.last_position || '-';
  }
  return row[col.field] ?? '-';
};

export default function TabTireInspection({
  vehicleDetails,
  companyId,
  installedTires,
  positionMap,
  hmUnit,
  kmUnit,
  date,
  selectedTire,
  onSelectTire,
  onTiresDataChange,
  isHmKmValid,
  limitInfo
}) {
  const notify = useNotifier();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState(0); // 0 = Tire List, 1 = Backlog
  const [inspectingTire, setInspectingTire] = useState(null); // null = list view, object = form view (add)
  const [inspectingBulkTires, setInspectingBulkTires] = useState(null);
  const [searchField, setSearchField] = useState('serial_number');
  const [searchValue, setSearchValue] = useState('');

  // Pagination for history
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);

  useEffect(() => {
    setHistoryPage(0);
  }, [installedTires, searchValue, searchField]);

  // Export state
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const exportOpen = Boolean(exportAnchorEl);

  // Fetch all inspection history from API (Moved to TireInspectionHistory)

  const { data: allBatches, isLoading: batchLoading } = useTireBatchList(0, 9999, companyId ? [{ value: companyId }] : []);

  // Queue state
  const [inspectionQueue, setInspectionQueue] = useState([]);
  const [isSubmittingQueue, setIsSubmittingQueue] = useState(false);

  const handleSubmitQueue = async () => {
    if (inspectionQueue.length === 0) return;
    setIsSubmittingQueue(true);

    let successfulIds = [];

    for (const item of inspectionQueue) {
      try {
        await createTireInspection(item.payload);
        successfulIds.push(item.id);
      } catch (error) {
        notify({
          message: `${t('tireCycle.failedToInspect', 'Failed to inspect')} ${item.tireData.serial_number}: ${error.response?.data?.message || error.message || 'Unknown error'}`,
          severity: 'error'
        });
      }
    }

    setInspectionQueue((q) => q.filter((item) => !successfulIds.includes(item.id)));

    if (successfulIds.length > 0) {
      notify({
        message: `${t('tireCycle.successfullySubmitted', 'Successfully submitted')} ${successfulIds.length} ${t('tireCycle.inspections', 'inspections')}`,
        severity: 'success'
      });
      revalidateTireBatchList();
      revalidateTireInspectionList();
    }

    setIsSubmittingQueue(false);
  };

  const installedSerialNumbers = useMemo(() => {
    if (!installedTires) return [];
    return Object.values(installedTires)
      .map((t) => t.serial_number)
      .filter(Boolean);
  }, [installedTires]);

  const filteredInspections = useMemo(() => {
    let filtered = [];

    // Filter by tab
    if (activeTab === 2) {
      filtered = Array.isArray(allBatches) ? allBatches : [];
      filtered = filtered.filter((row) => installedSerialNumbers.includes(row.serial_number));
      filtered = filtered.filter(
        (row) => row.last_inspection?.status_backlog && row.last_inspection?.status_backlog.trim() !== '' && row.is_need_action !== false
      );
      filtered = filtered.map((row) => {
        const { last_inspection, ...rest } = row;
        if (last_inspection) {
          const { ...liRest } = last_inspection;
          return { ...rest, ...liRest, id: row.id };
        }
        return rest;
      });
    }

    if (searchValue && searchField) {
      const lowerQ = searchValue.toLowerCase();
      filtered = filtered.filter((row) => {
        const val = row[searchField];
        return (val || '').toString().toLowerCase().includes(lowerQ);
      });
    }
    return filtered;
  }, [allBatches, installedSerialNumbers, searchValue, searchField, activeTab]);

  const inspectionRows = useMemo(() => {
    const start = historyPage * historyRowsPerPage;
    return filteredInspections.slice(start, start + historyRowsPerPage);
  }, [filteredInspections, historyPage, historyRowsPerPage]);

  const totalRecord = filteredInspections.length;

  // Build list of installed tires from parent state
  const tireList = useMemo(() => {
    if (!installedTires || !positionMap) return [];
    return Object.entries(installedTires).map(([posCode, tireData]) => ({
      posCode,
      posLabel: positionMap[posCode]?.label || posCode,
      serial_number: tireData.serial_number,
      tire_item: tireData.tire_item
    }));
  }, [installedTires, positionMap]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleInspectClick = (tire) => {
    setInspectingTire(tire);
    setInspectingBulkTires(null);
  };

  // ── Export Functions ──
  const fetchAllForExport = useCallback(async () => {
    return filteredInspections;
  }, [filteredInspections]);

  if (limitInfo && !limitInfo.allowedTx.includes('INSPECTION')) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error" fontWeight="bold">
          {t('tireCycle.blockedByBackdateRulesInspection', 'This transaction (INSPECTION) is blocked by backdate rules for this tire.')}
        </Typography>
      </Box>
    );
  }

  const handleInspectBulkClick = (selectedTires) => {
    setInspectingBulkTires(selectedTires);
    setInspectingTire(null);
  };

  const handleBackFromForm = () => {
    setInspectingTire(null);
    setInspectingBulkTires(null);
    revalidateTireInspectionList();
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';
    const c = status;
    if (c === 'LOW') return 'default';
    if (c === 'MEDIUM') return 'info';
    if (c === 'HIGH') return 'warning';
    if (c === 'CRITICAL') return 'error';
    return 'default';
  };

  const handleExportClick = (event) => setExportAnchorEl(event.currentTarget);
  const handleExportClose = () => setExportAnchorEl(null);

  const exportToCSV = async () => {
    setIsExporting(true);
    try {
      const dataToExport = await fetchAllForExport();
      const exportCols = getExportColumns(t);
      const formattedData = dataToExport.map((row) => {
        const rowValues = {};
        exportCols.forEach((col) => {
          rowValues[col.headerName] = getCellValueForExport(row, col);
        });
        return rowValues;
      });
      const csvData = Papa.unparse(formattedData);
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `tire-inspection-backlog-${getFormattedDate()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to export CSV:', err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      const exportCols = getExportColumns(t);
      const dataToExport = await fetchAllForExport();
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Tire Inspection');
      worksheet.columns = exportCols.map((col) => ({
        header: col.headerName,
        key: col.field,
        width: 20
      }));

      dataToExport.forEach((row) => {
        const rowValues = {};
        exportCols.forEach((col) => {
          rowValues[col.field] = getCellValueForExport(row, col);
        });
        worksheet.addRow(rowValues);
      });

      // Style header
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
      worksheet.eachRow((row) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        });
      });
      worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 1 }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `tire-inspection-backlog-${getFormattedDate()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to export Excel:', err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  const exportToPDF = async () => {
    setIsExporting(true);
    try {
      const exportCols = getExportColumns(t);
      const dataToExport = await fetchAllForExport();
      const doc = new jsPDF('landscape');
      const tableColumn = exportCols.map((col) => col.headerName);
      const tableRows = dataToExport.map((row) => {
        return exportCols.map((col) => {
          const val = getCellValueForExport(row, col);
          return val !== null && val !== undefined ? String(val) : '-';
        });
      });
      doc.setFontSize(16);
      doc.text(t('tireCycle.tireInspectionBacklog', 'Tire Inspection Backlog'), 14, 15);
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 20,
        theme: 'grid',
        styles: { fontSize: 8 },
        headStyles: { fillColor: [25, 118, 210] }
      });
      doc.save(`tire-inspection-backlog-${getFormattedDate()}.pdf`);
    } catch (err) {
      console.error('Failed to export PDF:', err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  // If user is inspecting a tire or editing, show the form
  if (inspectingTire) {
    return (
      <TabTireInspectionAddEdit
        onBack={handleBackFromForm}
        vehicleDetails={vehicleDetails}
        companyId={companyId}
        tireData={inspectingTire}
        hmUnit={hmUnit}
        kmUnit={kmUnit}
        date={date}
        queue={inspectionQueue}
        setQueue={setInspectionQueue}
        refetch={revalidateTireInspectionList}
      />
    );
  }

  if (inspectingBulkTires) {
    return (
      <TabTireBulkInspection
        selectedTires={inspectingBulkTires}
        onBack={handleBackFromForm}
        vehicleDetails={vehicleDetails}
        companyId={companyId}
        hmUnit={hmUnit}
        kmUnit={kmUnit}
        date={date}
        refetch={revalidateTireInspectionList}
      />
    );
  }

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="inspection tabs">
          <Tab label={t('tireCycle.installedTires', 'Installed Tires')} />
          <Tab label={t('tireCycle.installedTireInspectionHistory', 'Installed Tire Inspection History')} disabled={!isHmKmValid} />
          <Tab label={t('tireCycle.installedTireBacklog', 'Installed Tire Backlog')} disabled={!isHmKmValid} />
        </Tabs>
      </Box>

      {/* ── Tab 0: Installed Tires List ── */}
      {activeTab === 0 && (
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {!vehicleDetails ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                {t('tireCycle.selectVehicleToViewTires', 'Please select a vehicle to view installed tires.')}
              </Typography>
            </Box>
          ) : !isHmKmValid ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="warning.main" fontWeight="bold">
                {t(
                  'tireCycle.provideValidHmKm',
                  'Please provide valid HM and KM units (greater than or equal to the previous records) to view the inspection list.'
                )}
              </Typography>
            </Box>
          ) : tireList.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                {t('tireCycle.noTiresInstalled', 'No tires installed on this vehicle.')}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <TireActionList
                vehicleDetails={vehicleDetails}
                companyId={companyId}
                positionMap={positionMap}
                installedTires={installedTires}
                mode="inspect"
                onInspectClick={handleInspectClick}
                onInspectBulkClick={handleInspectBulkClick}
                selectedTire={selectedTire}
                onSelectTire={onSelectTire}
                onTiresDataChange={onTiresDataChange}
              />

              {inspectionQueue.length > 0 && (
                <Box sx={{ mt: 4, p: 2, bgcolor: '#f1f5f9', borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                    {t('tireCycle.inspectionQueue', 'Inspection Queue')} ({inspectionQueue.length})
                  </Typography>
                  <Stack spacing={2} sx={{ mb: 3 }}>
                    {inspectionQueue.map((item) => (
                      <Box
                        key={item.id}
                        sx={{
                          p: 2,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {t('tireCycle.tire', 'Tire:')} {item.tireData.serial_number} ({t('tireCycle.position', 'Position')}:{' '}
                            {item.tireData.posLabel || item.tireData.posCode})
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('general.condition', 'Condition:')} {item.payload.condition} | {t('general.tread', 'Tread:')}{' '}
                            {item.payload.tread_depth}mm | {t('general.pressure', 'Pressure:')} {item.payload.air_pressure}psi
                          </Typography>
                        </Box>
                        <Button
                          color="error"
                          size="small"
                          onClick={() => setInspectionQueue((q) => q.filter((i) => i.id !== item.id))}
                          disabled={isSubmittingQueue}
                        >
                          {t('general.remove', 'Remove')}
                        </Button>
                      </Box>
                    ))}
                  </Stack>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                    onClick={handleSubmitQueue}
                    disabled={isSubmittingQueue}
                  >
                    {isSubmittingQueue
                      ? t('general.submitting', 'Submitting...')
                      : t('tireCycle.confirmAllInspections', 'Confirm All Inspections')}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* ── Tab 1: Inspection History ── */}
      {activeTab === 1 && (
        <TireInspectionHistory vehicleDetails={vehicleDetails} companyId={companyId} installedSerialNumbers={installedSerialNumbers} />
      )}

      {/* ── Tab 2: Inspection Backlog ── */}
      {activeTab === 2 && (
        <Box>
          {/* Toolbar with Export */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {t('tireCycle.inspectionBacklog', 'Inspection Backlog')}
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select value={searchField} onChange={(e) => setSearchField(e.target.value)} displayEmpty>
                  <MenuItem value="serial_number">{t('tireRepairScrap.serialNumberExport', 'Serial Number')}</MenuItem>
                  <MenuItem value="status_backlog">{t('masterCompany.status', 'Status')}</MenuItem>
                  <MenuItem value="condition">{t('dashboardAsset.condition', 'Condition')}</MenuItem>
                  <MenuItem value="tire_damage_name">{t('tireRepairScrap.damage', 'Damage')}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                placeholder={t('dashboardAsset.search', 'Search...')}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                size="small"
                sx={{ width: 200 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
              <Box>
                <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleExportClick} disabled={isExporting}>
                  {isExporting ? t('general.exporting', 'Exporting...') : t('general.export', 'Export')}
                </Button>
                <Menu anchorEl={exportAnchorEl} open={exportOpen} onClose={handleExportClose}>
                  <MenuItem onClick={exportToCSV}>{t('tireCycle.exportToCSV', 'Export to CSV')}</MenuItem>
                  <MenuItem onClick={exportToExcel}>{t('tireCycle.exportToExcel', 'Export to Excel')}</MenuItem>
                  <MenuItem onClick={exportToPDF}>{t('tireCycle.exportToPDF', 'Export to PDF')}</MenuItem>
                </Menu>
              </Box>
            </Stack>
          </Stack>

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 250px)', overflow: 'auto' }}>
            <Table stickyHeader size="small" sx={{ minWidth: 1200, '& .MuiTableCell-root': { whiteSpace: 'nowrap', px: 2 } }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 5,
                      bgcolor: '#f1f5f9',
                      width: 120,
                      minWidth: 120,
                      maxWidth: 120,
                      boxSizing: 'border-box'
                    }}
                  >
                    {t('general.date', 'Date')}
                  </TableCell>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 120,
                      zIndex: 5,
                      bgcolor: '#f1f5f9',
                      width: 150,
                      minWidth: 150,
                      maxWidth: 150,
                      boxSizing: 'border-box',
                      borderRight: '2px solid #cbd5e1'
                    }}
                  >
                    {t('tireRepairScrap.serialNumberExport', 'Serial Number')}
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('menu.specification', 'Specification')}</TableCell>
                  <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('dashboardAsset.vehicle', 'Vehicle')}</TableCell>
                  <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('masterDriver.position', 'Position')}</TableCell>
                  <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('tireCardHistory.kmUnit', 'KM Unit')}</TableCell>
                  <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('tireCardHistory.hmUnit', 'HM Unit')}</TableCell>
                  <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('tireCardHistory.airPressurePsi', 'Air Pressure (psi)')}</TableCell>
                  <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('tireCardHistory.treadDepthMm', 'Tread Depth (mm)')}</TableCell>
                  <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('tireCardHistory.tireDamage', 'Damage')}</TableCell>
                  <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('tireRepairScrap.priority', 'Priority')}</TableCell>
                  <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('tireRepairScrap.mechanic', 'Mechanic')}</TableCell>
                  <TableCell sx={{ bgcolor: '#f1f5f9' }}>{t('tireRepairScrap.notes', 'Notes')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batchLoading ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : inspectionRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center">
                      {t('tireCycle.noInspectionDataAvailable', 'No inspection data available.')}
                    </TableCell>
                  </TableRow>
                ) : (
                  inspectionRows.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{
                        bgcolor: '#ffffff',
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          bgcolor: '#f8fafc !important',
                          '& .MuiTableCell-root': {
                            bgcolor: '#f8fafc !important'
                          }
                        }
                      }}
                    >
                      <TableCell
                        sx={{
                          whiteSpace: 'nowrap',
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          bgcolor: '#ffffff',
                          transition: 'background-color 0.2s',
                          width: 120,
                          minWidth: 120,
                          maxWidth: 120,
                          boxSizing: 'border-box'
                        }}
                      >
                        {row.inspection_date
                          ? dayjs(row.inspection_date).format('YYYY-MM-DD')
                          : row.created_date
                            ? dayjs(row.created_date).format('YYYY-MM-DD')
                            : '-'}
                      </TableCell>
                      <TableCell
                        sx={{
                          position: 'sticky',
                          left: 120,
                          zIndex: 2,
                          bgcolor: '#ffffff',
                          borderRight: '2px solid #cbd5e1',
                          transition: 'background-color 0.2s',
                          width: 150,
                          minWidth: 150,
                          maxWidth: 150,
                          boxSizing: 'border-box'
                        }}
                      >
                        <Typography variant="body2" fontWeight="bold">
                          {row.serial_number || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.join?.tire_item_tire_brand_name || '-'} / {row.join?.tire_item_tire_size_code || '-'} /{' '}
                        {row.join?.tire_item_tire_spec_name || '-'}
                      </TableCell>
                      <TableCell>{row.last_vehicle || row.join?.vehicle_license_plate || '-'}</TableCell>
                      <TableCell>{row.position || row.last_position || '-'}</TableCell>
                      <TableCell>{row.km_unit ?? '-'}</TableCell>
                      <TableCell>{row.hm_unit ?? '-'}</TableCell>
                      <TableCell>{row.air_pressure ?? '-'}</TableCell>
                      <TableCell>{row.tread_depth ?? '-'}</TableCell>
                      <TableCell>{row.tire_damage_name || '-'}</TableCell>
                      <TableCell>
                        <Chip label={row.status_backlog || '-'} size="small" color={getStatusColor(row.status_backlog)} />
                      </TableCell>
                      <TableCell>{row.inspector_name || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Tooltip title={row.notes || ''}>
                          <span>{row.notes || '-'}</span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalRecord || 0}
            page={historyPage}
            onPageChange={(e, newPage) => setHistoryPage(newPage)}
            rowsPerPage={historyRowsPerPage}
            onRowsPerPageChange={(e) => {
              setHistoryRowsPerPage(parseInt(e.target.value, 10));
              setHistoryPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50, 100, 1000]}
          />
        </Box>
      )}
    </Box>
  );
}
